import React, { useState } from 'react';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Textarea from '../../../components/ui/Textarea';
import CountrySelector from '../../../components/ui/CountrySelector';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import { getTimezoneByCountryName } from '../../../utils/countryTimezones';
import { identityVerificationService } from '../../../services/identityVerificationService';
import { useAuth } from '../../../hooks/useAuth';

const PersonalInfoTab = ({ 
  profileData, 
  fieldErrors, 
  onInputChange, 
  onProfilePictureUpload,
  onEmailChangeClick,
  emailChangeNotification,
  isUploadingPicture = false
}) => {
  const [imageError, setImageError] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [showVerificationModal, setShowVerificationModal] = useState(false);
  const [qrLinkInput, setQrLinkInput] = useState(profileData.identity_verification_qr_link || '');
  const [verificationSubmitting, setVerificationSubmitting] = useState(false);
  const [verificationError, setVerificationError] = useState('');
  const { user } = useAuth();
  
  const isFirstUpload = !profileData.profile_picture_change_count || profileData.profile_picture_change_count === 0;
  const hasPendingReview = profileData.profile_picture_status === 'pending';
  const wasRejected = profileData.profile_picture_status === 'rejected';
  
  // Show pending picture if available, otherwise show current picture
  // After approval, profile_picture_pending is cleared and profile_picture_status is set to 'none'
  // For old records with 'approved' status, treat as 'none'
  const displayPicture = profileData.profile_picture_pending || profileData.profile_picture;

  // Handle country change and automatically update timezone
  const handleCountryChange = (countryName) => {
    // Update location with the selected country
    onInputChange('location', countryName);
    
    // Automatically detect and set timezone based on country
    const timezone = getTimezoneByCountryName(countryName);
    if (timezone) {
      onInputChange('timezone', timezone);
    }
  };

  return (
    <div className="space-y-6">
      <div className="mb-8">
        <h2 className="text-xl font-semibold mb-1">Personal Information</h2>
        <p className="text-text-secondary text-sm">Update your basic profile information</p>
      </div>

      {/* Profile Picture */}
      <div className="mb-6">
        <label className="block mb-2 text-text-secondary font-medium">Profile Picture</label>
        <div className="flex items-center space-x-4">
          <div className="w-24 h-24 rounded-full overflow-hidden bg-background-secondary flex items-center justify-center relative">
            {displayPicture && !imageError ? (
              <>
                <Image 
                  src={displayPicture} 
                  alt="Profile" 
                  className="w-full h-full object-cover"
                  onLoad={() => {
                    setImageLoaded(true);
                    setImageError(false);
                  }}
                  onError={() => {
                    setImageError(true);
                  }}
                />
                {hasPendingReview && (
                  <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center">
                    <Icon name="Clock" size={24} className="text-white" />
                  </div>
                )}
              </>
            ) : (
              <Icon name="User" size={32} className="text-text-tertiary" />
            )}
          </div>
          <div className="flex-1">
            <Button
              variant="outline"
              iconName={isUploadingPicture ? "Loader" : "Upload"}
              onClick={() => {
                document.getElementById('profile-picture-upload').click();
              }}
              className="mb-2"
              disabled={hasPendingReview || isUploadingPicture}
            >
              {isUploadingPicture ? 'Uploading...' : (isFirstUpload ? 'Upload Photo' : 'Change Photo (Requires Review)')}
            </Button>
            <input
              type="file"
              id="profile-picture-upload"
              accept="image/*"
              className="hidden"
              onChange={(e) => {
                onProfilePictureUpload(e);
              }}
              disabled={hasPendingReview || isUploadingPicture}
            />
            
            {/* Status Messages */}
            {hasPendingReview && (
              <div className="flex items-center gap-2 text-amber-600 text-sm mb-2">
                <Icon name="Clock" size={16} />
                <span>Profile picture is pending admin review</span>
              </div>
            )}
            
            {wasRejected && (
              <div className="flex items-center gap-2 text-red-600 text-sm mb-2">
                <Icon name="AlertCircle" size={16} />
                <span>Previous upload was rejected. You can upload a new photo.</span>
              </div>
            )}
            
            {!isFirstUpload && !hasPendingReview && !wasRejected && (
              <div className="flex items-center gap-2 text-blue-600 text-sm mb-2">
                <Icon name="Info" size={16} />
                <span>Photo changes require admin approval</span>
              </div>
            )}
            
            <p className="text-text-tertiary text-xs">
              For best results, use a square image at least 400x400px (Make sure you upload a clear headshot with a plain background, moderation will be strict after the first upload).
            </p>
          </div>
        </div>
      </div>

      {/* Basic Info */}
      <div className="grid md:grid-cols-3 gap-6">
        <Input
          label="Full Name"
          required
          value={profileData.full_name || ''}
          onChange={(e) => onInputChange('full_name', e.target.value)}
          placeholder="Enter your full name"
          error={fieldErrors.full_name}
        />
        
        <div>
          <label className="block text-sm font-medium text-text-primary mb-2">
            Email Address <span className="text-red-500">*</span>
          </label>
          <div className="flex items-center gap-3">
            <Input
              value={profileData.email || ''}
              readOnly
              className="flex-1 bg-gray-50"
              placeholder="your.email@example.com"
            />
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={onEmailChangeClick}
              className="shrink-0"
            >
              Change
            </Button>
          </div>
          {emailChangeNotification && (
            <div className="mt-2 p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="flex items-center gap-2">
                <Icon name="Mail" size={16} className="text-blue-600 shrink-0" />
                <p className="text-sm text-blue-800">{typeof emailChangeNotification === 'string' ? emailChangeNotification : emailChangeNotification?.message}</p>
              </div>
            </div>
          )}
        </div>
        
        <Input
          label="Professional Title"
          value={profileData.title || ''}
          onChange={(e) => onInputChange('title', e.target.value)}
          placeholder="e.g. Executive Virtual Assistant"
          error={fieldErrors.title}
        />
        
        <Input
          label="Phone Number"
          value={profileData.phone || ''}
          onChange={(e) => onInputChange('phone', e.target.value)}
          placeholder="Enter your phone number"
          error={fieldErrors.phone}
        />
        
        <CountrySelector
          label="Location"
          required
          value={profileData.location || ''}
          onChange={handleCountryChange}
          placeholder="Select your country"
          error={fieldErrors.location}
        />
        
        <Input
          label="Education"
          value={profileData.education || ''}
          onChange={(e) => onInputChange('education', e.target.value)}
          placeholder="Highest degree or certification"
          error={fieldErrors.education}
        />
        
        <Input
          label="Video Introduction URL"
          value={profileData.video_introduction_url || ''}
          onChange={(e) => onInputChange('video_introduction_url', e.target.value)}
          placeholder="https://drive.google.com/file/... or direct video link"
          error={fieldErrors.video_introduction_url}
          type="url"
        />
        <div className="md:col-span-2">
          <Textarea
            label="Bio"
            rows={4}
            required
            value={profileData.bio || ''}
            onChange={(e) => onInputChange('bio', e.target.value)}
            placeholder="Tell us about yourself, your background, and your experience..."
            error={fieldErrors.bio}
            maxLength={1000}
          />
          <div className="text-right text-xs text-text-secondary mt-1">
            {(profileData.bio || '').length}/1000 characters
          </div>
        </div>
        
        <div className="md:col-span-3 bg-blue-50 border border-blue-200 rounded-lg p-4">          
          <div className="flex items-start gap-3">
            <Icon name="Info" size={20} className="text-blue-600 shrink-0 mt-0.5" />
            <div>
              <h4 className="font-medium text-blue-900 mb-1">Add a Video Introduction</h4>
              <p className="text-sm text-blue-800">Introduce yourself to employers with a personal video! Share a link to your video hosted on Google Drive or a direct video file link. An engaging video introduction can significantly increase your chances of landing the job.</p>
            </div>
          </div>
        </div>
      </div>

      {/* Identity Verification Section */}
      <div className="mt-8 border-t pt-6">
        <h2 className="text-xl font-semibold mb-1">Identity Verification</h2>
        <p className="text-text-secondary text-sm mb-4">Submit image of your ID to verify your identity. Verified VAs get a badge on their profile and get an 80% approval rate from employers.</p>

        {profileData.identity_verification_status === 'verified' ? (
          <div className="flex items-center gap-2 text-green-700 bg-green-50 border border-green-200 rounded-lg p-4">
            <Icon name="CheckCircle" size={20} className="text-green-600 shrink-0" />
            <div>
              <p className="font-medium">Your identity is verified!</p>
              <p className="text-sm text-green-600">Employers can see your verified badge.</p>
            </div>
          </div>
        ) : profileData.identity_verification_status === 'pending' ? (
          <div className="flex items-center gap-2 text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-4">
            <Icon name="Clock" size={20} className="text-amber-600 shrink-0" />
            <div>
              <p className="font-medium">Verification pending review</p>
              <p className="text-sm text-amber-600">An admin will review your submission shortly. Expect at least 24hrs to receive a response.</p>
            </div>
          </div>
        ) : (
          <>
            {profileData.identity_verification_status === 'rejected' && (
              <div className="flex items-center gap-2 text-red-700 bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
                <Icon name="AlertCircle" size={20} className="text-red-600 shrink-0" />
                <div>
                  <p className="font-medium">Verification was rejected</p>
                  {profileData.identity_verification_notes && (
                    <p className="text-sm text-red-600">Reason: {profileData.identity_verification_notes}</p>
                  )}
                  <p className="text-sm text-red-600">You can submit a new QR code link below.</p>
                </div>
              </div>
            )}
            <Button
              variant="outline"
              iconName="ShieldCheck"
              onClick={() => {
                setQrLinkInput(profileData.identity_verification_qr_link || '');
                setVerificationError('');
                setShowVerificationModal(true);
              }}
            >
              Verify Me!
            </Button>
          </>
        )}
      </div>

      {/* Verification QR Modal */}
      {showVerificationModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={() => setShowVerificationModal(false)}>
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4 shadow-xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Submit Image</h3>
              <button onClick={() => setShowVerificationModal(false)} className="p-1 hover:bg-gray-100 rounded">
                <Icon name="X" size={20} className="text-gray-500" />
              </button>
            </div>
            <p className="text-sm text-text-secondary mb-4">Note: We will only verify the QR code image if it matches with your national ID to your VA Sprouts profile and not store any personal information. You can also take a picture of your national ID and just blur any revealing information, except for your name and photo.</p> 

            <Input
              label="QR Code Image Link"
              value={qrLinkInput}
              onChange={(e) => setQrLinkInput(e.target.value)}
              placeholder="https://drive.google.com/file/your_image.jpg"
              type="url"
              error={verificationError}
            />

            {qrLinkInput && (
              <div className="mt-3 border rounded-lg p-2 bg-gray-50">
                <p className="text-xs text-gray-500 mb-2">Preview:</p>
                <p className="text-xs text-amber-600 mb-1">⚠ This image is loaded from the URL you provided.</p>
                <img
                  src={qrLinkInput}
                  alt="QR Code Preview"
                  className="max-h-40 mx-auto rounded"
                  referrerPolicy="no-referrer"
                  onError={(e) => { e.target.style.display = 'none'; }}
                />
              </div>
            )}

            <div className="flex justify-end gap-3 mt-6">
              <Button variant="outline" onClick={() => setShowVerificationModal(false)}>
                Cancel
              </Button>
              <Button
                disabled={verificationSubmitting || !qrLinkInput.trim()}
                onClick={async () => {
                  try {
                    setVerificationSubmitting(true);
                    setVerificationError('');
                    // Basic URL validation
                    try { new URL(qrLinkInput.trim()); } catch { 
                      setVerificationError('Please enter a valid URL');
                      setVerificationSubmitting(false);
                      return;
                    }
                    await identityVerificationService.submitQRLink(user.id, qrLinkInput.trim());
                    onInputChange('identity_verification_status', 'pending');
                    onInputChange('identity_verification_qr_link', qrLinkInput.trim());
                    setShowVerificationModal(false);
                  } catch (err) {
                    setVerificationError(err.message || 'Failed to submit. Please try again.');
                  } finally {
                    setVerificationSubmitting(false);
                  }
                }}
              >
                {verificationSubmitting ? 'Submitting...' : 'Submit for Review'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PersonalInfoTab;

